import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import FormEmbed from "@/components/FormEmbed";
import { siteConfig } from "@/lib/site-config";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Keyboard contract for the dialog: Escape closes it, focus moves into the
  // panel on open and returns to the trigger on close, and Tab cycles within
  // the panel instead of escaping to the page behind it.
  useEffect(() => {
    if (!open) {
      lastFocused.current?.focus();
      lastFocused.current = null;
      return;
    }
    lastFocused.current = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
        ) ?? []
      ).filter((el) => el.offsetParent !== null);

    focusable()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeQuoteForm();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/65 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto overscroll-contain"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            {/* my-auto centres the panel when it fits and keeps it fully scrollable when it doesn't */}
            <m.div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-form-title"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-lg my-auto widget-shell flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-3rem)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Kept inside the panel — .widget-shell clips overflow, so a negative offset would hide it */}
              <button
                onClick={closeQuoteForm}
                className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full bg-secondary text-secondary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
                aria-label="Close form"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="bg-card px-6 pt-6 pb-2 pr-16 shrink-0">
                <h3 id="quote-form-title" className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Tell us about your project — we'll reply within hours.
                </p>
              </div>

              {/* Scrolls independently so the form's own buttons stay reachable on short screens */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-card">
                <FormEmbed id={`popup-${siteConfig.widgets.formId}`} className="p-4 sm:p-6" />
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
